import React, {useEffect, useRef, useState} from 'react'
import {Button} from "primereact/button";
import {BREITE_HANDY} from "./statistikWerte";
import {getFromLocalStorageWithDefault, saveToLocalStorage} from "../../../../util/localStorageZugriff";

// Zoom von 50 % bis 150 % in 5-%-Schritten
export const ZOOM_STUFEN = Array.from({length: 21}, (_, i) => (50 + i * 5) / 100);
export const ZOOM_START_INDEX = ZOOM_STUFEN.indexOf(1);
export const ZOOM_HANDY_INDEX = ZOOM_STUFEN.indexOf(0.75);

/**
 * Zoom-Zustand der Turnierbäume, gilt für die normale Ansicht und das Vollbild. Auf Handys startet der Baum bei 75 %,
 * damit mehr davon auf den Bildschirm passt.
 */
export function useBaumZoom() {
    return useState(() => window.matchMedia("(max-width: " + BREITE_HANDY + "px)").matches ? ZOOM_HANDY_INDEX : ZOOM_START_INDEX);
}

const KEY_LIVE_ZOOM = "dh_public_live_zoom";

// ohne gemerkten Wert: in der App und auf Handys 80 %, sonst 100 %
const startLiveZoom = (appCall) => appCall || window.matchMedia("(max-width: " + BREITE_HANDY + "px)").matches ? ZOOM_STUFEN.indexOf(0.8) : ZOOM_START_INDEX;

const ladeLiveZoom = (appCall) => {
    try {
        const gespeichert = getFromLocalStorageWithDefault(KEY_LIVE_ZOOM, undefined);
        return Number.isInteger(gespeichert) && gespeichert >= 0 && gespeichert < ZOOM_STUFEN.length ? gespeichert : startLiveZoom(appCall);
    } catch (e) {
        // ohne Speicher (z.B. privater Modus) gilt der Startwert
        return startLiveZoom(appCall);
    }
}

/**
 * Zoom der Live-Spiele (Live-Bereich der öffentlichen Ansicht und eigenständige Live-Seite), wird im Browser gemerkt.
 * Gemerkt wird erst eine Änderung durch den Benutzer, nicht der Startwert (sonst bliebe z.B. der Handy-Wert hängen).
 * @returns [zoomIndex, setzeZoom]
 */
export function useLiveZoom(appCall) {
    const [zoomIndex, setZoomIndex] = useState(() => ladeLiveZoom(appCall));
    const setzeZoom = (aenderung) => setZoomIndex(alt => {
        const neu = typeof aenderung === "function" ? aenderung(alt) : aenderung;
        try {
            saveToLocalStorage(KEY_LIVE_ZOOM, neu);
        } catch (e) {
            // ohne Speicher (z.B. privater Modus) gilt der Zoom nur bis zum Neuladen
        }
        return neu;
    });
    return [zoomIndex, setzeZoom];
}

// gedrückt halten: nach kurzer Wartezeit wird wiederholt in größeren Sprüngen (10 %) gezoomt
const WARTEZEIT_MS = 400;
const WIEDERHOLUNG_MS = 150;
const SPRUNG_GEHALTEN = 2;

/**
 * Knöpfe Verkleinern/Vergrößern mit Anzeige des Zoomwerts für die Bäume, in der normalen Ansicht und im Vollbild.
 * Ein Klick ändert um 5 %, gedrückt halten in 10-%-Sprüngen.
 */
export default function ZoomSteuerung({zoomIndex, setZoomIndex, klein = false}) {

    const timer = useRef(undefined);
    const intervall = useRef(undefined);
    // nach dem Halten soll der abschließende Klick nicht noch einen weiteren Schritt machen
    const wurdeGehalten = useRef(false);

    const aendern = (schritte) => {
        setZoomIndex(index => Math.min(ZOOM_STUFEN.length - 1, Math.max(0, index + schritte)));
    }

    const stoppen = () => {
        clearTimeout(timer.current);
        clearInterval(intervall.current);
        timer.current = undefined;
        intervall.current = undefined;
    }

    useEffect(() => {
        // losgelassen wird evtl. außerhalb des Knopfs oder auf dem inzwischen gesperrten Knopf
        const loslassen = () => {
            stoppen();
            // erst nach dem Klick zurücksetzen, der direkt auf das Loslassen folgt
            setTimeout(() => wurdeGehalten.current = false, 0);
        };
        window.addEventListener("pointerup", loslassen);
        window.addEventListener("pointercancel", loslassen);
        return () => {
            stoppen();
            window.removeEventListener("pointerup", loslassen);
            window.removeEventListener("pointercancel", loslassen);
        };
    }, []);

    useEffect(() => {
        if (zoomIndex === 0 || zoomIndex === ZOOM_STUFEN.length - 1) {
            stoppen();
        }
    }, [zoomIndex]);

    const halten = (richtung) => {
        stoppen();
        wurdeGehalten.current = false;
        timer.current = setTimeout(() => {
            wurdeGehalten.current = true;
            aendern(richtung * SPRUNG_GEHALTEN);
            intervall.current = setInterval(() => aendern(richtung * SPRUNG_GEHALTEN), WIEDERHOLUNG_MS);
        }, WARTEZEIT_MS);
    }

    const klick = (richtung) => {
        if (wurdeGehalten.current) {
            wurdeGehalten.current = false;
            return;
        }
        aendern(richtung);
    }

    const getKnopf = (richtung, icon, label, gesperrt) => {
        // klein: passend zum Vollbild-Knopf in der Leiste über dem Baum
        return <Button icon={icon} aria-label={label} className={"dhpt-zoom-knopf" + (klein ? " p-button-sm p-button-outlined dhpt-button-hell" : "")} disabled={gesperrt}
                       onPointerDown={(event) => event.button === 0 && halten(richtung)}
                       onClick={() => klick(richtung)}
                       onContextMenu={(event) => event.preventDefault()}/>
    }

    return <>
        {getKnopf(-1, "pi pi-search-minus", "Verkleinern", zoomIndex === 0)}
        <span className="dhpt-zoom-wert">{Math.round(ZOOM_STUFEN[zoomIndex] * 100)}&nbsp;%</span>
        {getKnopf(1, "pi pi-search-plus", "Vergrößern", zoomIndex === ZOOM_STUFEN.length - 1)}
    </>
}
